import { useMemo, useState } from 'react';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  SelectField,
  toast,
} from '@eon/ui';
import { hooks } from '@eon/api-client';
import type { Device } from '@eon/types';
import { Search } from 'lucide-react';

const PAGE_SIZE = 50;

interface AddGroupDeviceDialogProps {
  groupId: string;
  linkedDeviceIds: Set<string>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddGroupDeviceDialog({
  groupId,
  linkedDeviceIds,
  open,
  onOpenChange,
}: AddGroupDeviceDialogProps) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [deviceId, setDeviceId] = useState<string | undefined>(undefined);
  const list = hooks.useAdminDevices({ q, page, pageSize: PAGE_SIZE });
  const assign = hooks.useAssignGroupDevice();

  const candidates = useMemo(() => {
    const rows = list.data?.data ?? [];
    return rows.filter((d) => {
      if (linkedDeviceIds.has(d.id)) return false;
      if (d.organizationId && d.organizationId !== groupId) return false;
      return true;
    });
  }, [list.data?.data, linkedDeviceIds, groupId]);

  const options = candidates.map((d) => ({
    value: d.id,
    label: d.displayName?.trim() || d.serialNumber,
    description: deviceDescription(d, t),
  }));

  const submit = async () => {
    if (!deviceId) {
      toast.error(t('group.device.selectRequired'));
      return;
    }
    try {
      await assign.mutateAsync({ groupId, deviceId });
      toast.success(t('group.device.assigned'));
      setDeviceId(undefined);
      setQ('');
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('group.device.addTitle')}</DialogTitle>
          <DialogDescription>{t('group.device.addDescription')}</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="text-muted-foreground absolute top-2.5 left-3 h-4 w-4" />
          <Input
            className="pl-9"
            placeholder={t('actions.search')}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <SelectField
          value={deviceId}
          onValueChange={setDeviceId}
          options={options}
          placeholder={t('group.device.selectPlaceholder')}
          disabled={list.isLoading || options.length === 0}
        />
        {list.data && (list.data.totalPages ?? 1) > 1 ? (
          <div className="flex justify-between text-sm">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t('pagination.previous')}
            </Button>
            <span className="text-muted-foreground self-center">
              {page} / {list.data.totalPages ?? 1}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page >= (list.data.totalPages ?? 1)}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('pagination.next')}
            </Button>
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('actions.cancel')}
          </Button>
          <Button type="button" onClick={submit} disabled={assign.isPending}>
            {t('group.actions.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function deviceDescription(d: Device, t: (key: string) => string) {
  const parts = [d.serialNumber];
  if (d.stationId) parts.push(t('group.device.linkedStation'));
  return parts.join(' · ');
}
